import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { api_url } from "../../../apiUtils";

// Fetches the R2 / R3 enable flags of the logged-in user's referred (upper) user.
// R1 is always available and never depends on this.
export const useReferrerRatioFlags = () => {
  const token = sessionStorage.getItem("accesstoken");
  const [referrerRatioFlags, setReferrerRatioFlags] = useState({
    r2: false,
    r3: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchReferrerRatioFlags = async () => {
      try {
        const response = await axios.get(
          `${api_url}ratio-setting/my-referrer-ratio`,
          {
            headers: {
              accesstoken: token,
            },
          },
        );

        if (!isMounted) {
          return;
        }

        const flags = response?.data?.referrerRatioSetting;
        setReferrerRatioFlags({
          r2: Boolean(flags?.r2),
          r3: Boolean(flags?.r3),
        });
      } catch (error) {
        if (isMounted) {
          setReferrerRatioFlags({ r2: false, r3: false });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchReferrerRatioFlags();

    return () => {
      isMounted = false;
    };
  }, [token]);

  return { referrerRatioFlags, loading };
};

const ratioOrder = ["R1", "R2", "R3"];
const ratioGameOrder = [
  "open",
  "jodi",
  "openSinglePana",
  "openDoublePana",
  "openTriplePana",
  "choukada",
  "sangam",
  "close",
  "closeSinglePana",
  "closeDoublePana",
  "closeTriplePana",
];

const ratioGameLabels = {
  open: "Open",
  jodi: "Jodi",
  openSinglePana: "Open Single Pana",
  openDoublePana: "Open Double Pana",
  openTriplePana: "Open Triple Pana",
  choukada: "Choukada",
  sangam: "Sangam",
  close: "Close",
  closeSinglePana: "Close Single Pana",
  closeDoublePana: "Close Double Pana",
  closeTriplePana: "Close Triple Pana",
};

export const useRatioSettings = () => {
  const token = sessionStorage.getItem("accesstoken");
  const [ratioSettings, setRatioSettings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchRatioSettings = async () => {
      try {
        const response = await axios.get(
          `${api_url}ratio-setting/get-ratio-setting`,
          {
            headers: {
              accesstoken: token,
            },
          },
        );

        if (!isMounted) {
          return;
        }

        setRatioSettings(
          Array.isArray(response?.data?.ratioSettings)
            ? response.data.ratioSettings
            : [],
        );
      } catch (error) {
        if (isMounted) {
          setRatioSettings([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchRatioSettings();

    return () => {
      isMounted = false;
    };
  }, [token]);

  return { ratioSettings, loading };
};

export const getRatioMinimumBet = (ratioSettings, ratio, gameKey) => {
  const matchedSetting = ratioSettings.find((setting) => setting.ratio === ratio);
  if (!matchedSetting) {
    return 0;
  }

  const matchedGameSetting = Array.isArray(matchedSetting.settings)
    ? matchedSetting.settings.find((setting) => setting.gameKey === gameKey)
    : null;

  const fallbackValue = Number(matchedSetting?.minimumBetAmint ?? 0);
  const parsedValue = Number(matchedGameSetting?.minimumBetAmint ?? fallbackValue);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

function RatioSettingsSelector({
  value,
  onChange,
  minimumBet,
  showMinimumBet = true,
}) {
  const displayMinimumBet = useMemo(() => Number(minimumBet || 0), [minimumBet]);

  const { referrerRatioFlags } = useReferrerRatioFlags();

  // R1 is always available; R2 / R3 depend on the referred user's flags.
  const availableRatios = useMemo(
    () =>
      ratioOrder.filter((ratio) => {
        if (ratio === "R2") return Boolean(referrerRatioFlags?.r2);
        if (ratio === "R3") return Boolean(referrerRatioFlags?.r3);
        return true;
      }),
    [referrerRatioFlags],
  );

  // If the currently selected ratio is no longer available, fall back to R1.
  useEffect(() => {
    if (value && !availableRatios.includes(value)) {
      onChange("R1");
    }
  }, [value, availableRatios, onChange]);

  return (
    <Box sx={{ mt: 3, mb: 2 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>
        Select Ratio
      </Typography>
      <RadioGroup
        row
        value={value}
        onChange={(event) => onChange(event.target.value)}
        sx={{ gap: 2, flexWrap: "wrap" }}
      >
        {availableRatios.map((ratio) => (
          <FormControlLabel
            key={ratio}
            value={ratio}
            control={<Radio />}
            label={ratio}
          />
        ))}
      </RadioGroup>
      {showMinimumBet && (
        <Typography sx={{ mt: 1, color: "#000000", fontSize: 18, fontWeight: 600 }}>
          Minimum Bet / Number : {displayMinimumBet}
        </Typography>
      )}
    </Box>
  );
}

export const ratioGameConfig = ratioGameOrder.map((gameKey) => ({
  gameKey,
  label: ratioGameLabels[gameKey] || gameKey,
}));

// Mirrors backend's checkPana() in math-backend/controllers/Calculation/Calculation.js
export const checkPanaType = (pana) => {
  const digits = String(pana).split("").map(Number);
  if (digits[1] === 0 && digits[2] === 0 && digits[0] === 0) return "TP";
  if (
    (digits[2] === 0 && digits[0] < digits[1]) ||
    (digits[0] < digits[1] && digits[1] < digits[2])
  ) {
    return "SP";
  } else if (
    (digits[1] === 0 && digits[2] === 0) ||
    (digits[0] === digits[1] && digits[0] < digits[2]) ||
    (digits[1] === digits[2] && digits[0] < digits[1]) ||
    (digits[0] === digits[1] && digits[1] !== digits[2])
  ) {
    return "DP";
  } else {
    return "TP";
  }
};

export default RatioSettingsSelector;
